/**
 * Vertical seat for a sink relative to its countertop.
 *
 * The renderer places a sink GLB with its bounding-box CENTRE at `center.y`
 * (`SceneObject.jsx` re-origins every GLB on its bbox centre and then uses
 * `object.center` verbatim for sinks). So seating a sink means deciding where
 * its bbox centre goes, and that differs per mount:
 *
 *   undermount  bbox TOP flush with the underside of the slab; basin and
 *               tailpiece hang into the cabinet.
 *   dropin      bbox TOP flush with the UPPER surface — a drop-in rests on
 *               the counter on its rim, one slab-thickness higher than an
 *               undermount. Treating it as an undermount sinks it out of
 *               sight, leaving the rim below the counter.
 *   vessel      sits entirely ON the counter. With a measured `vessel-bottom`
 *               ring we know exactly how far up the model that contact
 *               surface is; without one we fall back to resting the bbox
 *               bottom on the slab.
 *
 * Extracted so creation (`assembleVanity`) and the swap path
 * (`fixtureManipulation.swapSinkModel`) cannot drift: they previously carried
 * near-identical copies of this arithmetic that disagreed on unit handling and
 * on fallbacks, which is the bug family that produced a vessel offset stranded
 * on an undermount basin.
 *
 * All inputs and the return value are METRES, except `vesselRingOffsetInches`,
 * which mirrors the `yOffsetFromBottomInches` field the GLB pipeline writes.
 *
 * @param {Object} params
 * @param {string} params.sinkType - canonical mount
 * @param {number} params.modelHeightMeters
 * @param {number} params.countertopTopY
 * @param {number} params.countertopBottomY
 * @param {number} [params.vesselRingOffsetInches]
 * @returns {number|null} centre Y, or null if the inputs are unusable
 */
function calculateSinkSeatY({
  sinkType,
  modelHeightMeters,
  countertopTopY,
  countertopBottomY,
  vesselRingOffsetInches,
}) {
  const INCHES_TO_METERS = 0.0254;
  if (
    !Number.isFinite(modelHeightMeters) ||
    !Number.isFinite(countertopTopY) ||
    !Number.isFinite(countertopBottomY)
  ) {
    return null;
  }

  const half = modelHeightMeters / 2;

  if (sinkType === 'vessel') {
    if (Number.isFinite(vesselRingOffsetInches)) {
      // Lift the model so its contact ring — not its bbox bottom — rests on
      // the slab.
      return countertopTopY + half - vesselRingOffsetInches * INCHES_TO_METERS;
    }
    return countertopTopY + half;
  }

  if (sinkType === 'dropin') {
    return countertopTopY - half;
  }

  // Undermount, and anything unrecognised: hang beneath the slab. This is the
  // conservative default — it is what every non-vessel sink did before
  // drop-ins were given their own branch.
  return countertopBottomY - half;
}

/**
 * How far a vessel's RIM stands above the counter it is seated on: the bowl
 * `calculateSinkSeatY` seats, read at its top instead of its centre. A
 * wall-mount faucet over a vessel has to clear it, and taking the rim from
 * the seat rule means the two cannot disagree about where the bowl is.
 *
 * @param {Object} params
 * @param {number} params.modelHeightMeters
 * @param {number} [params.vesselRingOffsetInches]
 * @returns {number|null} metres, or null if the height is unusable
 */
function vesselRimAboveCounter({ modelHeightMeters, vesselRingOffsetInches }) {
  const centre = calculateSinkSeatY({
    sinkType: 'vessel',
    modelHeightMeters,
    countertopTopY: 0,
    countertopBottomY: 0,
    vesselRingOffsetInches,
  });
  return centre === null ? null : centre + modelHeightMeters / 2;
}

module.exports = { calculateSinkSeatY, vesselRimAboveCounter };
